/**
 * Deep compare should compare all top-level keys and recursively
 * compare nested object. It should return an array of paths that
 * differ between the target and source objects.
 *
 * Usage:
 *
 *    const source = { a: 1, b: { c: 1 } };
 *    const target = { a: 2, b: { c: 2 }, d: 3 };
 *    const diff = deepCompare(source, target);
 *    => ['a', 'b.c', 'd']
 */
import isPrimitive from './is-primitive';

export default function deepCompare(source, target) {
  const keys = [];

  // if target is a primitive type, we cannot compare
  // so exit early with empty array
  if (isPrimitive(target) || target === undefined) {
    return keys;
  }

  // if source is a primitive type, exit early
  if (isPrimitive(source) || source === undefined) {
    // if target is object we treat all top-level keys as changed
    if (Object(target) === target) {
      return Object.keys(target);
    }
    return keys;
  }

  Object.keys(target).forEach(key => {
    const sourceValue = source[key];
    const targetValue = target[key];

    // eslint-disable-next-line
    if (!source.hasOwnProperty(key)) {
      keys.push(key);
    } else if (isPrimitive(sourceValue) && !isPrimitive(targetValue)) {
      keys.push(key);
    } else if (isPrimitive(targetValue)) {
      if (targetValue !== sourceValue) {
        keys.push(key);
      }
    } else if (Object(targetValue) === targetValue) {
      deepCompare(sourceValue, targetValue).forEach(deepKey => keys.push(`${key}.${deepKey}`));
    }
  });

  return keys;
}
